import { useEffect, useMemo, useState } from "react";

import { Skeleton } from "@/components/ui/Skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/Tooltip";
import { cn } from "@/lib/utils";
import type { TypeDetail } from "@/customer/catalog/types";

import { PriceBreakdown } from "./PriceBreakdown";
import { useQuote } from "./api";
import type { QuoteInput } from "./types";

// Date-range picker + add-on toggles that drive a live PriceBreakdown.
// Pure preview; the Reserve button owns the transactional hand-off to
// checkout. Native <input type="date"> for iOS/Android calendar UX;
// debounced 250ms so keystroke-driven changes don't hammer the server.

interface Props {
  type: TypeDetail;
  // Dates are owned by the parent so they can be carried into the
  // cart on Reserve. Add-ons + waiver + delivery toggle remain
  // estimator-local — they don't carry forward today.
  startDate: string;
  endDate: string;
  onStartDateChange: (next: string) => void;
  onEndDateChange: (next: string) => void;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function todayPlusNoticeDays(noticeHours: number): string {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const noticeDays = Math.ceil(noticeHours / 24);
  now.setDate(now.getDate() + noticeDays);
  return now.toISOString().slice(0, 10);
}

function isoPlusDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

// Phase 4c rate-card overhaul (FR-3, FR-5) — quotes are always whole-week
// multiples; the 7-day floor is enforced by the server.
export const MIN_RENTAL_DAYS = 7;

// Seed dates the estimator would otherwise pick on its own — exposed
// for parents that need to lift state up.
export function defaultEstimatorRange(
  noticeHours: number,
  minRentalDays: number = MIN_RENTAL_DAYS
): { start: string; end: string } {
  const start = todayPlusNoticeDays(noticeHours);
  const end = isoPlusDays(start, minRentalDays - 1);
  return { start, end };
}

export function QuoteEstimator({
  type,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange
}: Props): JSX.Element {
  const defaultStart = useMemo(() => todayPlusNoticeDays(type.notice_required_hours), [type.notice_required_hours]);
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState<number[]>([]);
  const [damageWaiver, setDamageWaiver] = useState(false);
  const [delivery, setDelivery] = useState(true);

  // The type may not allow pickup at all — force delivery=true in that case.
  useEffect(() => {
    if (type.delivery_only && !delivery) setDelivery(true);
  }, [type.delivery_only, delivery]);

  // Debounce the input before triggering the query.
  const debounced = useDebounced({
    startDate,
    endDate,
    selectedAttachmentIds,
    damageWaiver,
    delivery
  }, 250);

  const datesAreValid =
    ISO_DATE.test(debounced.startDate) && ISO_DATE.test(debounced.endDate);

  const quoteInput: QuoteInput | null = datesAreValid
    ? {
        type_slug: type.slug,
        start_date: debounced.startDate,
        end_date: debounced.endDate,
        // Sort so that toggling A then B produces the same react-query
        // cache key as toggling B then A — otherwise we double-fetch.
        attachment_type_ids: [...debounced.selectedAttachmentIds].sort((a, b) => a - b),
        damage_waiver: debounced.damageWaiver,
        delivery: debounced.delivery
      }
    : null;

  const { data, isPending, isFetching } = useQuote(quoteInput);

  return (
    <section
      aria-labelledby="estimate-heading"
      className="rounded-md border border-zinc-200 bg-brand-white p-4 space-y-4"
    >
      <h2 id="estimate-heading" className="text-base font-semibold text-brand-black">
        Quick estimate
      </h2>

      <div className="grid gap-3 sm:grid-cols-2">
        <DateField
          id="estimate-start"
          label="Pickup / delivery date"
          value={startDate}
          min={defaultStart}
          onChange={onStartDateChange}
        />
        <DateField
          id="estimate-end"
          label="Return date"
          value={endDate}
          min={startDate}
          onChange={onEndDateChange}
        />
      </div>

      <AvailabilityBadge
        startDate={startDate}
        endDate={endDate}
        earliestLegalStart={defaultStart}
        minRentalDays={MIN_RENTAL_DAYS}
      />

      {type.attachments.length > 0 ? (
        <fieldset className="space-y-1">
          <legend className="text-sm font-medium text-brand-black">Add-on attachments</legend>
          <ul className="space-y-1">
            {type.attachments.map((att) => (
              <li key={att.id}>
                <label className="flex items-center gap-2 text-sm text-brand-black">
                  <input
                    type="checkbox"
                    checked={selectedAttachmentIds.includes(att.id)}
                    onChange={(e) => {
                      setSelectedAttachmentIds((prev) =>
                        e.target.checked ? [...prev, att.id] : prev.filter((id) => id !== att.id)
                      );
                    }}
                  />
                  <span>{att.name}</span>
                </label>
              </li>
            ))}
          </ul>
        </fieldset>
      ) : null}

      {type.damage_waiver_percent > 0 ? (
        <div className="flex items-center gap-2 text-sm text-brand-black">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={damageWaiver}
              onChange={(e) => setDamageWaiver(e.target.checked)}
            />
            <span>
              Add damage waiver ({Math.round(type.damage_waiver_percent * 100)}% of rental)
            </span>
          </label>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="What is the damage waiver?"
                className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-zinc-300 text-xs font-semibold text-zinc-500 hover:border-brand-red hover:text-brand-red focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-1"
              >
                ?
              </button>
            </TooltipTrigger>
            <TooltipContent
              className="max-w-xs text-left text-xs leading-relaxed"
              data-testid="damage-waiver-tooltip"
            >
              Covers accidental damage during your rental. Skip it if your own
              liability policy already extends to rented equipment — upload a
              certificate of insurance at checkout instead.
            </TooltipContent>
          </Tooltip>
        </div>
      ) : null}

      {type.pickup_allowed && !type.delivery_only ? (
        <label className="flex items-center gap-2 text-sm text-brand-black">
          <input
            type="checkbox"
            checked={!delivery}
            onChange={(e) => setDelivery(!e.target.checked)}
          />
          <span>I&apos;ll pick this up myself (skip delivery fee)</span>
        </label>
      ) : null}

      <div aria-busy={isFetching}>
        {!datesAreValid ? (
          <p className="text-sm text-zinc-500">Pick both dates to see your estimate.</p>
        ) : isPending && !data ? (
          <Skeleton className="h-32 w-full" />
        ) : null}
        {/* Live region wraps only the meaningful result so screen readers
            announce the price update rather than the transient skeleton. */}
        <div aria-live="polite">
          {datesAreValid && data ? <PriceBreakdown quote={data} /> : null}
        </div>
      </div>
    </section>
  );
}

function DateField({
  id,
  label,
  value,
  min,
  onChange
}: {
  id: string;
  label: string;
  value: string;
  min: string;
  onChange: (next: string) => void;
}): JSX.Element {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-brand-black">
        {label}
      </label>
      <input
        id={id}
        type="date"
        value={value}
        min={min}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-brand-red focus:outline-none focus:ring-1 focus:ring-brand-red"
      />
    </div>
  );
}

// Sits below the date inputs, reassuring customers that the dates they
// just picked actually work before they hit Reserve. We can't promise
// per-unit availability without a yard binding (that happens during
// service-area resolution in /cart), so the badge surfaces the only
// honest answers we have at this stage: notice-window respected, range
// meets the weekly floor, dates parse.
const ISO_PRETTY = /^(\d{4})-(\d{2})-(\d{2})$/;
function formatPretty(iso: string): string {
  const m = ISO_PRETTY.exec(iso);
  if (!m) return iso;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function AvailabilityBadge({
  startDate,
  endDate,
  earliestLegalStart,
  minRentalDays,
}: {
  startDate: string;
  endDate: string;
  earliestLegalStart: string;
  minRentalDays: number;
}): JSX.Element | null {
  if (!ISO_DATE.test(startDate) || !ISO_DATE.test(endDate)) return null;

  // Strict ISO yyyy-mm-dd compares lexicographically.
  if (startDate < earliestLegalStart) {
    return (
      <BadgeShell
        tone="warn"
        testId="availability-badge"
        label={`Earliest pickup is ${formatPretty(earliestLegalStart)}`}
        detail="We need lead time to route a unit and confirm delivery."
      />
    );
  }

  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  const days = Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1;
  if (days < minRentalDays) {
    return (
      <BadgeShell
        tone="warn"
        testId="availability-badge"
        label={`Minimum rental is ${minRentalDays} days`}
        detail="Rates are billed in whole-week increments."
      />
    );
  }

  return (
    <BadgeShell
      tone="ok"
      testId="availability-badge"
      label="Available for these dates"
      detail="We'll confirm the exact unit at delivery scheduling."
    />
  );
}

function BadgeShell({
  tone,
  label,
  detail,
  testId,
}: {
  tone: "ok" | "warn";
  label: string;
  detail: string;
  testId: string;
}): JSX.Element {
  return (
    <div
      data-testid={testId}
      data-tone={tone}
      role="status"
      className={cn(
        "flex items-start gap-2 rounded-md border px-3 py-2 text-sm",
        tone === "ok"
          ? "border-emerald-200 bg-emerald-50 text-emerald-900"
          : "border-amber-200 bg-amber-50 text-amber-900"
      )}
    >
      <span aria-hidden="true" className="mt-0.5 font-semibold">
        {tone === "ok" ? "✓" : "⚠"}
      </span>
      <div>
        <p className="font-medium">{label}</p>
        <p className="text-xs opacity-80">{detail}</p>
      </div>
    </div>
  );
}

// Lightweight debounce so each keystroke in the date input doesn't fire
// a quote. We snapshot the whole input object together so all fields
// settle into the same downstream query.
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}
